import { useEffect, useRef } from 'react';

export const CustomCursor = () => {
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const animationFrameId = useRef<number | null>(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const current = useRef({ x: 0, y: 0 });
  const visibleRef = useRef(false);
  const idleTimeout = useRef<number | null>(null);
  const runningRef = useRef(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // Respect reduced motion by smoothing less, but do not disable the cursor
    const smoothing = reduceMotion ? 0.35 : 0.18;

    const el = cursorRef.current;
    const label = labelRef.current;
    if (!el) return;

    const setHover = (v: boolean) => {
      el.style.width = v ? '48px' : '32px';
      el.style.height = v ? '48px' : '32px';
      if (label) {
        label.style.opacity = '0';
        label.style.transform = 'scale(0)';
      }
    };

    const setVisible = (v: boolean) => {
      visibleRef.current = v;
      el.style.opacity = v ? '0.95' : '0'; // high opacity when visible
    };

    const animate = () => {
      const lerp = (s: number, e: number, f: number) => s + (e - s) * f;
      current.current.x = lerp(current.current.x, mousePos.current.x, smoothing);
      current.current.y = lerp(current.current.y, mousePos.current.y, smoothing);
      el.style.transform = `translate3d(${current.current.x - 16}px, ${current.current.y - 16}px, 0)`;

      const dx = Math.abs(current.current.x - mousePos.current.x);
      const dy = Math.abs(current.current.y - mousePos.current.y);
      const dist = dx + dy;

      if (dist > 0.5 || visibleRef.current) {
        animationFrameId.current = requestAnimationFrame(animate);
      } else {
        runningRef.current = false;
      }
    };

    const onMove = (e: MouseEvent) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
      if (!visibleRef.current) setVisible(true);
      const target = e.target as HTMLElement;
      const hover = !!target?.closest?.('[data-cursor-hover]');
      setHover(hover);

      if (!runningRef.current) {
        runningRef.current = true;
        animationFrameId.current = requestAnimationFrame(animate);
      }

      if (idleTimeout.current) window.clearTimeout(idleTimeout.current);
      idleTimeout.current = window.setTimeout(() => {
        setHover(false);
        setVisible(false);
      }, 1500);
    };

    window.addEventListener('mousemove', onMove);
    const onEnter = () => setVisible(true);
    const onLeave = () => setVisible(false);
    window.addEventListener('mouseenter', onEnter);
    window.addEventListener('mouseleave', onLeave);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseenter', onEnter);
      window.removeEventListener('mouseleave', onLeave);
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
      if (idleTimeout.current) window.clearTimeout(idleTimeout.current);
    };
  }, []);

  return (
    <div
      ref={cursorRef}
      className="fixed pointer-events-none z-[9999] hidden md:block"
      style={{
        width: '32px',
        height: '32px',
        borderRadius: '9999px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)', // almost fully opaque dark circle
        border: '1px solid rgba(255, 255, 255, 0.2)', // subtle light border for visibility
        opacity: 0,
        transform: 'translate3d(-100px,-100px,0)',
        transition: 'width 300ms ease, height 300ms ease, opacity 200ms ease',
        mixBlendMode: 'normal',
      }}
    >
      <span
        ref={labelRef}
        style={{
          color: '#ffffff',
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: 'scale(0)',
          opacity: 0,
          transition: 'transform 200ms ease, opacity 200ms ease',
        }}
      >
        View
      </span>
    </div>
  );
};
